import axios from '../../api/axiosInstance';
import { STATUS, extractErrorInfo } from './sharedfunction';

const SIGNUP_BASE = '/signup';

/**
 * Request signup verification email (magic link).
 * @param {{ email: string }} params
 * @returns {Promise<{ status: number, data?: object, message?: string, error?: string, errors?: object }>}
 */
export async function requestSignupVerification({ email } = {}) {
  try {
    if (!email || typeof email !== 'string' || !email.trim()) {
      return {
        status: STATUS.ERROR,
        error: 'Email is required',
      };
    }

    const response = await axios.post(`${SIGNUP_BASE}/request-verification`, {
      email: email.trim().toLowerCase(),
    });

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data ?? {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message ?? response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    return extractErrorInfo(error);
  }
}

/**
 * Verify signup token (from magic link). Email is not passed; API returns it in response.
 * @param {{ token: string }} params
 * @returns {Promise<{ status: number, data?: { valid: boolean, email: string }, message?: string, error?: string, errors?: object }>}
 */
export async function verifySignupToken({ token } = {}) {
  try {
    if (!token || typeof token !== 'string' || !token.trim()) {
      return {
        status: STATUS.ERROR,
        error: 'Token is required',
      };
    }

    const response = await axios.get(`${SIGNUP_BASE}/verify`, {
      params: { token: token.trim() },
    });

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data ?? {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message ?? response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    return extractErrorInfo(error);
  }
}

/**
 * Complete signup: create org + user + org_plan + org_usage_summary (session set by backend).
 * Email is resolved from verificationToken on the backend; pass the token from the verify step.
 * @param {{ verificationToken: string, orgName: string, fullName: string, password: string, tosLegalDocumentVersionId: string, privacyLegalDocumentVersionId: string }} params
 * @returns {Promise<{ status: number, data?: object, message?: string, error?: string, errors?: object }>}
 */
export async function completeSignup({
  verificationToken,
  orgName,
  fullName,
  password,
  tosLegalDocumentVersionId,
  privacyLegalDocumentVersionId,
} = {}) {
  try {
    if (
      !verificationToken ||
      !orgName ||
      !fullName ||
      !password ||
      !tosLegalDocumentVersionId ||
      !privacyLegalDocumentVersionId
    ) {
      return {
        status: STATUS.ERROR,
        error: 'Verification link, organization name, full name, password, and legal consent are required',
      };
    }

    const response = await axios.post(`${SIGNUP_BASE}/complete`, {
      verificationToken: verificationToken.trim(),
      orgName: orgName.trim(),
      fullName: fullName.trim(),
      password,
      tos_legal_document_version_id: tosLegalDocumentVersionId,
      privacy_legal_document_version_id: privacyLegalDocumentVersionId,
    }, {
      withCredentials: true,
    });

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data ?? {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message ?? response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    return extractErrorInfo(error);
  }
}
